import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import styles from './Hero.module.css';

const HERO_SLIDES = [
  {
    eyebrow: 'WELCOME TO OUR CHURCH',
    prefix: 'WELCOME TO',
    title: 'FAITH FELLOWSHIP CHURCH',
  },
  {
    eyebrow: 'VISION',
    prefix: 'Raise the Remnant. Carry the Fire.',
    title: 'Reveal Christ.',
  },
  {
    eyebrow: 'MISSION',
    prefix: 'Reach. Restore. Raise.',
    title: 'Release. Revive. Send.',
  },
  {
    eyebrow: 'HEALING & RESTORATION',
    prefix: 'DISCOVER',
    title: 'HEALING, HOPE & NEW LIFE',
  },
  {
    eyebrow: 'DISCIPLESHIP & FAITH',
    prefix: 'TRANSFORMING LIVES',
    title: 'THROUGH THE POWER OF FAITH',
  },
  {
    eyebrow: 'JOIN US THIS WEEK',
    prefix: 'WORSHIP WITH US',
    title: 'FRIDAYS 8:15 PM IN RUWI',
  },
];

export default function Hero() {
  const [loaded] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const [dummyActiveBtn, setDummyActiveBtn] = useState(null);

  const heroCardRef = useRef(null);
  const eyebrowRef = useRef(null);
  const prefixRef = useRef(null);
  const titleRef = useRef(null);
  const isTransitioningRef = useRef(false);
  const toastTimeoutRef = useRef(null);

  const handleDummyClick = (btnName) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setDummyActiveBtn(btnName);
    toastTimeoutRef.current = setTimeout(() => {
      setDummyActiveBtn(null);
    }, 2400);
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const heroVideoRef = useRef(null);
  const hasNotified = useRef(false);

  const notifyMotionReady = () => {
    if (hasNotified.current) return;
    hasNotified.current = true;
    window.dispatchEvent(new CustomEvent('church:motion-ready'));
  };

  // Video playback management & scroll optimization
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    
    const ensurePlay = () => {
      if (video.paused && video.readyState >= 2) {
        video.play().catch(() => {});
      }
    };

    const onStall = () => {
      video.load();
      video.play().catch(() => {});
    };

    video.addEventListener('canplay', ensurePlay);
    video.addEventListener('loadeddata', ensurePlay);
    video.addEventListener('stalled', onStall);
    video.addEventListener('waiting', ensurePlay);
    video.addEventListener('error', onStall);
    ensurePlay();

    // Watchdog: kick video every 3s if paused and visible
    const watchdog = setInterval(() => {
      if (video.paused && !document.hidden) {
        video.play().catch(() => {});
      }
    }, 3000);

    const onWake = () => {
      ensurePlay();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    // Pause video when scrolled out of view to ensure 60fps/120fps butter-smooth scrolling
    let observer = null;
    if ('IntersectionObserver' in window && heroCardRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            ensurePlay();
          } else {
            if (!video.paused) {
              video.pause();
            }
          }
        },
        { threshold: 0.05 }
      );
      observer.observe(heroCardRef.current);
    }

    // Safety notification so splash screen dismisses quickly
    const readyTimer = setTimeout(() => {
      notifyMotionReady();
    }, 150);

    return () => {
      clearTimeout(readyTimer);
      clearInterval(watchdog);
      if (observer) observer.disconnect();
      video.removeEventListener('canplay', ensurePlay);
      video.removeEventListener('loadeddata', ensurePlay);
      video.removeEventListener('stalled', onStall);
      video.removeEventListener('waiting', ensurePlay);
      video.removeEventListener('error', onStall);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  // Slide Switch Handler — Still in-place crossfade without vertical jumping
  const goToSlide = newIndex => {
    if (newIndex === slideIndex || isTransitioningRef.current) return;
    isTransitioningRef.current = true;

    // If tab is in background, change state instantly so background progression continues smoothly
    if (document.hidden) {
      isTransitioningRef.current = false;
      setSlideIndex(newIndex);
      return;
    }

    const tl = gsap.timeline({
      onComplete: () => {
        isTransitioningRef.current = false;
        setSlideIndex(newIndex);
      },
    });

    tl.to([eyebrowRef.current, prefixRef.current, titleRef.current], {
      opacity: 0,
      duration: 0.24,
      ease: 'power2.inOut',
    });
  };

  // GSAP Animate In on slideIndex change — Fixed position stationary fade-in
  useEffect(() => {
    if (!loaded) return;

    isTransitioningRef.current = false;

    // Clear any previous transform to keep position strictly stationary
    gsap.set([eyebrowRef.current, prefixRef.current, titleRef.current], {
      clearProps: 'transform,y,yPercent',
    });

    const tl = gsap.timeline();

    tl.fromTo(
      [eyebrowRef.current, prefixRef.current, titleRef.current],
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.42,
        stagger: 0.04,
        ease: 'power2.out',
      }
    );

    const timer = setTimeout(() => {
      goToSlide((slideIndex + 1) % HERO_SLIDES.length);
    }, 5000);

    return () => {
      clearTimeout(timer);
      tl.kill();
    };
  }, [slideIndex, loaded]);

  const currentSlide = HERO_SLIDES[slideIndex];

  return (
    <section className={styles.heroSection}>
      <div className={styles.heroContainer}>
        <div ref={heroCardRef} className={`${styles.heroCard} ${loaded ? styles.cardLoaded : ''}`}>
          {/* Ambient Church Video Background */}
          <div
            className={styles.videoContainer}
            style={{ backgroundImage: "url('/assets/videos/motion3-poster.webp')" }}
          >
            <video
              ref={heroVideoRef}
              className={styles.videoBg}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              disablePictureInPicture
              disableRemotePlayback
              poster="/assets/videos/motion3-poster.webp"
              onPlaying={notifyMotionReady}
              onLoadedData={notifyMotionReady}
            >
              <source src="/assets/videos/Motion3.mp4" type="video/mp4" />
            </video>
            <div className={styles.videoFallback} />
          </div>

          {/* Cinematic Overlay Scrim */}
          <div className={styles.overlayScrim} />

          <div className={styles.content}>
            {/* Slide Index Progress Indicator */}
            <div className={`${styles.slideIndicators} ${loaded ? styles.visibleIndicators : ''}`}>
              {HERO_SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`${styles.indicatorBar} ${idx === slideIndex ? styles.activeIndicator : ''}`}
                  onClick={() => goToSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                >
                  <span
                    key={idx === slideIndex ? `fill-active-${slideIndex}` : `fill-inactive-${idx}`}
                    className={styles.barFill}
                  />
                </button>
              ))}
            </div>

            {/* Still Main Headline in Fixed Position */}
            <div className={styles.headlineWrapper}>
              <span ref={eyebrowRef} className={`section-eyebrow ${styles.slideEyebrow}`}>
                {currentSlide.eyebrow}
              </span>
              <h1 className={styles.heading}>
                <span ref={prefixRef} className={styles.prefixLine}>
                  {currentSlide.prefix}
                </span>
                <strong ref={titleRef} className={styles.titleLine}>
                  {currentSlide.title}
                </strong>
              </h1>
            </div>

            <div className={`${styles.row} ${loaded ? styles.rowVisible : ''}`}>
              <div className={styles.cta}>
                <div className={styles.dummyBtnWrapper}>
                  <button
                    type="button"
                    className={`btn-gold ${styles.dummyBtn}`}
                    onClick={() => handleDummyClick('visit')}
                    title="Plan Your Visit — Coming Soon"
                    aria-label="Plan Your Visit — Coming Soon"
                  >
                    PLAN YOUR VISIT
                  </button>
                  {dummyActiveBtn === 'visit' && (
                    <div className={styles.comingSoonBubble} role="status">
                      <span className={styles.bubbleDot} />
                      <span>Coming Soon</span>
                    </div>
                  )}
                </div>

                <div className={styles.dummyBtnWrapper}>
                  <button
                    type="button"
                    className={`btn-white ${styles.dummyBtn}`}
                    onClick={() => handleDummyClick('live')}
                    title="Live Services — Coming Soon"
                    aria-label="Live Services — Coming Soon"
                  >
                    LIVE SERVICES
                  </button>
                  {dummyActiveBtn === 'live' && (
                    <div className={styles.comingSoonBubble} role="status">
                      <span className={styles.bubbleDot} />
                      <span>Coming Soon</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.liveBadge}>
                  <span className={styles.liveDot} />
                  <span>WEEKLY WORSHIP</span>
                </div>
                <p className={styles.info}>
                  <strong className={styles.infoTitle}>Fridays 8:15 PM – 10:15 PM</strong>
                  <span className={styles.infoAddr}>Old Main Hall, PCO Campus, Ruwi</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}




